/**
 * SystemComponentVisualizer
 * Main component that combines all visualizations for a heating system
 */

import { SystemSchematic } from './SystemSchematic';
import { ThermalResponseGraph } from './ThermalResponseGraph';
import { SystemProsCons } from './SystemProsCons';
import { getSystemLayout } from './types';
import type { SystemVisualModel } from '../../../../../src/core/types';

interface SystemComponentVisualizerProps {
  systemType: string;
  systemTitle?: string;
  flowRate?: number;
  printMode?: boolean;
  /** New: Visual model from recommendation engine (optional for backward compatibility) */
  visualModel?: SystemVisualModel;
}

export function SystemComponentVisualizer({ 
  systemType, 
  systemTitle,
  flowRate,
  printMode = false,
  visualModel
}: SystemComponentVisualizerProps) {
  // Use visual model if available, otherwise fall back to legacy layout
  const layout = visualModel ? null : getSystemLayout(systemType);
  
  // If no visual model and no legacy layout, show error
  if (!visualModel && !layout) {
    return (
      <div style={{ 
        padding: '24px', 
        textAlign: 'center',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
      }}>
        <p>System visualization not available for: {systemType}</p>
      </div>
    );
  }
  
  return (
    <div 
      className={printMode ? 'system-visualizer-print' : 'system-visualizer'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%',
        maxWidth: printMode ? '210mm' : '100%',
        margin: '0 auto',
        padding: printMode ? '12mm' : '0',
      }}
    >
      {/* Header */}
      {systemTitle && (
        <div style={{
          textAlign: 'center',
          paddingBottom: '12px',
          borderBottom: '2px solid #2563eb',
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#1e293b',
            margin: '0 0 8px 0',
          }}>
            {systemTitle}
          </h2>
          <div style={{
            fontSize: '14px',
            color: '#64748b',
            fontWeight: 500,
          }}>
            {visualModel 
              ? `${visualModel.topology.systemCategory.replace('_', ' ')} System`
              : layout ? `${layout.systemType} System` : ''}
          </div>
        </div>
      )}
      
      {/* Visual Components */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: printMode ? '1fr 1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
      }}>
        {/* System Schematic */}
        <SystemSchematic layout={layout} visualModel={visualModel} />
        
        {/* Thermal Response Graph */}
        <ThermalResponseGraph layout={layout} visualModel={visualModel} />
      </div>
      
      {/* Pros and Cons Summary */}
      <SystemProsCons layout={layout} flowRate={flowRate} visualModel={visualModel} />
      
      {/* Print page break after each system */}
      {printMode && (
        <div className="print-page-break" style={{ pageBreakAfter: 'always' }} />
      )}
    </div>
  );
}

// Export sub-components for individual use
export { SystemSchematic } from './SystemSchematic';
export { ThermalResponseGraph } from './ThermalResponseGraph';
export { SystemProsCons } from './SystemProsCons';
export { getSystemLayout, SYSTEM_LAYOUTS } from './types';
export type { SystemLayout, SystemComponent, CapacityInfo } from './types';
